<!-- components/FileUploader.vue -->
<template>
  <b-card class="upload-card">
    <template #header>
      <div class="text-center">
        <h2 class="mb-0">
          <i class="fas fa-cloud-upload-alt text-primary"></i>
          {{ title }}
        </h2>
        <p v-if="subtitle" class="text-muted mt-1 mb-0">{{ subtitle }}</p>
      </div>
    </template>

    <!-- Alert: Video file size guideline -->
    <b-alert variant="warning" show class=" p-1" >
      <p class="mb-1">
        <strong>حجم فایل ویدیویی برای هر دقیقه حداکثر ۱۰ مگابایت باشد.</strong>
      </p>
      <p class="mb-1">
        به‌عنوان مثال، اگر مدت زمان یک فایل آموزشی ۱۰ دقیقه باشد، حجم نهایی آن نباید بیشتر از
        <strong>۱۰۰ مگابایت</strong> باشد.
      </p>
      <p class="mb-0">
        این محدودیت با توجه به شرایط اینترنت در ایران در نظر گرفته شده است؛ زیرا حجم فایل ویدیو تأثیر مستقیمی بر
        <strong>سرعت بارگذاری، پخش و تماشای محتوا</strong> دارد و کاربران ممکن است در نقاط مختلف کشور و با سرعت‌های متفاوت
        اینترنت به محتوای آموزشی دسترسی داشته باشند.
      </p>
      <p class="mb-0 mt-2">
        بنابراین، لطفاً هنگام خروجی گرفتن ویدیو، ضمن حفظ کیفیت مناسب، حجم فایل را به‌گونه‌ای تنظیم کنید که
        <strong>به ازای هر دقیقه حداکثر ۱۰ مگابایت</strong> باشد.
      </p>
    </b-alert>

    <b-form @submit.prevent="handleUpload">
      <!-- File Input with Drop Zone -->
      <b-form-group
          label="فایل"
          label-for="fileInput"
          label-class="required-label"
      >
        <b-form-file
            id="fileInput"
            v-model="file"
            :state="Boolean(file)"
            placeholder="برای انتخاب فایل کلیک کنید یا فایل را بکشید و اینجا رها کنید"
            drop-placeholder="فایل را اینجا رها کنید..."
            accept="*/*"
            class="file-drop-zone"
            :file-name-formatter="formatFileName"
            :disabled="isUploading"
        />
      </b-form-group>

      <!-- Media Type & Title Row -->
      <b-row>
        <!-- Media type dropdown is now commented out; detection is automatic -->
        <!--
        <b-col cols="12" md="6">
          <b-form-group
              label="نوع رسانه"
              label-for="mediaType"
              label-class="required-label"
          >
            <b-form-select
                id="mediaType"
                v-model="form.mediaType"
                :options="mediaTypeOptions"
                :disabled="isUploading"
            />
          </b-form-group>
        </b-col>
        -->
        <b-col cols="12" md="6">
          <b-form-group
              label="عنوان"
              label-for="title"
          >
            <b-form-input
                id="title"
                v-model="form.title"
                placeholder="عنوان فایل را وارد کنید"
                :disabled="isUploading"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group
              label="نوع رسانه"
              label-for="mediaTypeDisplay"
          >
            <b-form-input
                id="mediaTypeDisplay"
                :value="mediaTypeLabel"
                disabled
                plaintext
                class="form-control-plaintext"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Alt Text -->
      <b-form-group
          label="متن جایگزین (Alt)"
          label-for="alt"
      >
        <b-form-input
            id="alt"
            v-model="form.alt"
            placeholder="توضیح مختصر برای سئو"
            :disabled="isUploading"
        />
      </b-form-group>

      <!-- Description -->
      <b-form-group
          label="توضیحات"
          label-for="description"
      >
        <b-form-textarea
            id="description"
            v-model="form.description"
            placeholder="توضیحات کامل فایل"
            rows="3"
            :disabled="isUploading"
        />
      </b-form-group>

      <!-- Tags -->
      <b-form-group
          label="برچسب‌ها (با کاما جدا کنید)"
          label-for="tags"
      >
        <b-form-input
            id="tags"
            v-model="form.tags"
            placeholder="مثلاً: طبیعت, شهری, تابستان"
            :disabled="isUploading"
        />
      </b-form-group>

      <!-- Attach to Owner (optional) -->
      <div v-if="showAttach" class="attach-section">
        <hr />
        <h5 class="mb-3"><i class="fas fa-link text-primary"></i> اتصال به موجودیت</h5>
        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="نوع موجودیت" label-for="ownerType">
              <b-form-select
                  id="ownerType"
                  v-model="form.ownerType"
                  :options="ownerTypeOptions"
                  :disabled="isUploading"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="شناسه موجودیت" label-for="objectId">
              <b-form-input
                  id="objectId"
                  v-model.number="form.objectId"
                  type="number"
                  placeholder="مثلاً 123"
                  :disabled="isUploading"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="ترتیب" label-for="sortOrder">
              <b-form-input
                  id="sortOrder"
                  v-model.number="form.sortOrder"
                  type="number"
                  placeholder="۰"
                  :disabled="isUploading"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-checkbox
            v-model="form.isMain"
            switch
            :disabled="isUploading"
        >
          به عنوان اصلی
        </b-form-checkbox>
      </div>

      <!-- Upload Progress -->
      <div v-if="isUploading" class="upload-progress-wrapper">
        <b-progress
            :value="uploadProgress"
            :max="100"
            variant="primary"
            class="upload-progress"
            height="20px"
            striped
            animated
            show-value
            :label="`${uploadProgress}%`"
        />
        <p class="text-muted text-center mt-1">
          <i class="fas fa-spinner fa-spin"></i>
          در حال آپلود... لطفاً صبر کنید
        </p>
      </div>

      <!-- Submit Button -->
      <b-button
          type="submit"
          variant="primary"
          block
          size="lg"
          class="upload-submit"
          :disabled="!file || isUploading || (showAttach && !form.objectId)"
      >
        <i class="fas fa-upload"></i>
        {{ submitText }}
      </b-button>
    </b-form>
  </b-card>
</template>

<script>
import axios from 'axios'
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BProgress,
  BRow
} from 'bootstrap-vue'
import ToastificationContent from "@core/components/toastification/ToastificationContent"

export default {
  name: 'FileUploader',
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BRow,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormFile,
    BFormCheckbox,
    BProgress
  },
  props: {
    title: {
      type: String,
      default: 'آپلود فایل جدید'
    },
    subtitle: {
      type: String,
      default: 'فایل خود را انتخاب کنید و اطلاعات متادیتا را تکمیل نمایید.'
    },
    submitText: {
      type: String,
      default: 'آپلود فایل'
    },
    showAttach: {
      type: Boolean,
      default: false
    },
    defaultOwnerType: {
      type: Number,
      default: 1 // 1 = Product
    },
    autoRedirect: {
      type: Boolean,
      default: true
    },
    redirectRoute: {
      type: String,
      default: 'apps-library-list'
    },
    // Override API base if needed
    apiBase: {
      type: String,
      default: 'https://lenzavaapi.devsimagar.com/api/MediaLibrary'
    }
  },
  emits: ['upload-success', 'upload-error', 'upload-progress'],
  data() {
    return {
      file: null,
      form: {
        mediaType: 2, // default video (will be updated automatically)
        title: '',
        alt: '',
        description: '',
        tags: '',
        optimizeImage: true,
        extractThumbnail: true,
        convertToHls: true,
        generatePreview: true,
        isSecure: false,
        // Attach fields
        ownerType: this.defaultOwnerType,
        objectId: null,
        isMain: false,
        sortOrder: 0
      },
      // mediaTypeOptions kept for reference but not used in template
      mediaTypeOptions: [
        { value: 1, text: 'تصویر' },
        { value: 2, text: 'ویدیو' },
        { value: 3, text: 'صوتی' },
        { value: 4, text: 'سند' }
      ],
      ownerTypeOptions: [
        { value: 1, text: 'محصول' },
        { value: 2, text: 'وبلاگ' },
        { value: 3, text: 'پست' },
        { value: 4, text: 'صفحه' },
        { value: 5, text: 'پروفایل کاربر' },
        { value: 6, text: 'پادکست' }
      ],
      isUploading: false,
      uploadProgress: 0
    }
  },
  computed: {
    mediaTypeLabel() {
      const found = this.mediaTypeOptions.find(opt => opt.value === this.form.mediaType)
      return found ? found.text : 'ناشناخته'
    }
  },
  watch: {
    file(newFile) {
      if (newFile) {
        this.detectMediaType(newFile)
      }
    }
  },
  methods: {
    detectMediaType(file) {
      const type = file.type // MIME type, e.g., 'image/png', 'video/mp4'
      let mediaType = 4 // default document

      if (type) {
        if (type.startsWith('image/')) {
          mediaType = 1
        } else if (type.startsWith('video/')) {
          mediaType = 2
        } else if (type.startsWith('audio/')) {
          mediaType = 3
        } else {
          // fallback to extension
          const ext = file.name.split('.').pop().toLowerCase()
          const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico']
          const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm']
          const audioExts = ['mp3', 'wav', 'aac', 'ogg', 'flac']
          if (imageExts.includes(ext)) mediaType = 1
          else if (videoExts.includes(ext)) mediaType = 2
          else if (audioExts.includes(ext)) mediaType = 3
        }
      } else {
        // If MIME type is not available, use extension
        const ext = file.name.split('.').pop().toLowerCase()
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico']
        const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm']
        const audioExts = ['mp3', 'wav', 'aac', 'ogg', 'flac']
        if (imageExts.includes(ext)) mediaType = 1
        else if (videoExts.includes(ext)) mediaType = 2
        else if (audioExts.includes(ext)) mediaType = 3
      }

      this.form.mediaType = mediaType
    },
    formatFileName(files) {
      if (files && files.length > 0) {
        const file = files[0]
        return `${file.name} (${this.formatFileSize(file.size)})`
      }
      return ''
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      const val = (bytes / Math.pow(k, i)).toFixed(2)
      return `${val} ${sizes[i]}`
    },
    resetForm() {
      this.file = null
      this.form.title = ''
      this.form.alt = ''
      this.form.description = ''
      this.form.tags = ''
      this.form.objectId = null
      this.form.isMain = false
      this.form.sortOrder = 0
      // Reset media type to default? We'll set to video as fallback.
      this.form.mediaType = 2
    },
    async handleUpload() {
      if (!this.file) {
        this.$toast({
          component: ToastificationContent,
          position: 'bottom-center',
          props: {
            title: 'خطا',
            icon: 'XIcon',
            variant: 'warning',
            text: 'لطفاً یک فایل انتخاب کنید.'
          }
        })
        return
      }

      if (this.showAttach && !this.form.objectId) {
        this.$toast({
          component: ToastificationContent,
          position: 'bottom-center',
          props: {
            title: 'خطا',
            icon: 'XIcon',
            variant: 'warning',
            text: 'لطفاً شناسه موجودیت را وارد کنید.'
          }
        })
        return
      }

      this.isUploading = true
      this.uploadProgress = 0

      const formData = new FormData()
      formData.append('File', this.file)
      formData.append('MediaType', this.form.mediaType)
      formData.append('Title', this.form.title || this.file.name)
      formData.append('Alt', this.form.alt || '')
      formData.append('Description', this.form.description || '')
      formData.append('Tags', this.form.tags || '')
      formData.append('OptimizeImage', this.form.optimizeImage ? 'true' : 'false')
      formData.append('ExtractThumbnail', this.form.extractThumbnail ? 'true' : 'false')
      formData.append('ConvertToHls', this.form.convertToHls ? 'true' : 'false')
      formData.append('GeneratePreview', this.form.generatePreview ? 'true' : 'false')
      formData.append('IsSecure', this.form.isSecure ? 'true' : 'false')

      if (this.showAttach) {
        formData.append('OwnerType', this.form.ownerType)
        formData.append('ObjectId', this.form.objectId)
        formData.append('IsMain', this.form.isMain ? 'true' : 'false')
        formData.append('SortOrder', this.form.sortOrder || 0)
      }

      const token = localStorage.getItem('accessToken') || ''
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }

      const endpoint = this.showAttach ? '/upload-and-attach' : '/upload'
      const url = `${this.apiBase}${endpoint}`

      try {
        const response = await axios.post(url, formData, {
          headers,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            this.uploadProgress = percentCompleted
            this.$emit('upload-progress', percentCompleted)
          }
        })

        this.isUploading = false
        this.uploadProgress = 0

        this.$toast({
          component: ToastificationContent,
          position: 'bottom-center',
          props: {
            title: 'موفق',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'فایل با موفقیت آپلود شد! 🎉'
          }
        })

        this.$emit('upload-success', response.data)

        this.resetForm()

        if (this.autoRedirect) {
          this.$router.push({ name: this.redirectRoute })
        }

      } catch (error) {
        this.isUploading = false
        this.uploadProgress = 0
        console.error('Upload error:', error)
        const errorMessage = error.response?.data?.errorMessage || error.message || 'مشکل در آپلود فایل'
        this.$toast({
          component: ToastificationContent,
          position: 'bottom-center',
          props: {
            title: 'خطا در آپلود',
            icon: 'XIcon',
            variant: 'danger',
            text: errorMessage
          }
        })
        this.$emit('upload-error', error)
      }
    }
  }
}
</script>

<style scoped>
.upload-card {
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
}
.upload-card >>> .card-header {
  background: transparent;
  border-bottom: none;
  padding-bottom: 0;
}
.upload-card >>> .card-body {
  padding: 2rem;
}
.required-label::after {
  content: ' *';
  color: #ef4444;
}
.file-drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 18px;
  padding: 0.75rem;
  background: #fafbfc;
  transition: all 0.25s ease;
}
.file-drop-zone >>> .custom-file-input:focus ~ .custom-file-label {
  border-color: #6366f1;
  box-shadow: 0 0 0 0.2rem rgba(99, 102, 241, 0.25);
}
.file-drop-zone >>> .custom-file-label {
  border: none;
  background: transparent;
}
.file-drop-zone >>> .custom-file-label::after {
  border: none;
  background: #6366f1;
  color: #fff;
  border-radius: 0 14px 14px 0;
}
.upload-options {
  margin: 1.5rem 0;
  padding: 1rem 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}
.attach-section {
  margin: 1.5rem 0;
}
.upload-progress-wrapper {
  margin: 1.5rem 0;
}
.upload-progress {
  border-radius: 30px;
  background: #e2e8f0;
}
.upload-progress >>> .progress-bar {
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.85rem;
}
.upload-submit {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 16px;
  font-weight: 700;
  padding: 0.75rem 1rem;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
  transition: all 0.25s ease;
}
.upload-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.4);
}
.upload-submit:disabled {
  opacity: 0.6;
}
</style>
